import { parseDecimal } from '@catering/ui';
import { z } from 'zod';

const unitPriceSchema = z
  .string()
  .trim()
  .min(1, 'El precio es obligatorio')
  .refine((value) => (parseDecimal(value) ?? -1) >= 0, {
    message: 'Ingresá un precio válido, por ejemplo 1500,50',
  })
  .transform((value) => Math.round((parseDecimal(value) ?? 0) * 100));

/**
 * The options come from `/vat-rates/options` (`contract-prices/spec.md`,
 * "Selector de alícuota"), so which rates are valid is the catalog's call, and
 * the API enforces it (`VAT_RATE_NOT_SUPPORTED` / `VAT_RATE_INACTIVE`); here
 * only the shape is checked.
 */
const vatRateSchema = z
  .string()
  .min(1, 'Seleccioná una alícuota')
  .refine((value) => Number.isFinite(Number(value)) && Number(value) >= 0, {
    message: 'Seleccioná una alícuota válida',
  })
  .transform(Number);

/**
 * Mirrors `apps/api` `addContractPriceSchema`/`updateContractPriceSchema`
 * (`contract-price.dto.ts`), with the same pesos-to-cents transform
 * `menu-form-schema.ts`'s `basePrice` already applies: a person types
 * pesos, the API's integer-cents contract is not something a form field
 * should ask them to compute by hand.
 */
export const contractPriceFormSchema = z.object({
  menuId: z.string().min(1, 'Seleccioná un menú').uuid({ message: 'Seleccioná un menú' }),
  unitPrice: unitPriceSchema,
  vatRate: vatRateSchema,
  validFrom: z.string().trim().min(1, 'La vigencia desde es obligatoria'),
  validTo: z.string().trim(),
  isAvailable: z.boolean(),
});

/** Raw, unvalidated shape bound to the inputs: `unitPrice`/`vatRate` are still strings a person types/selects. */
export interface ContractPriceFormFields {
  menuId: string;
  unitPrice: string;
  vatRate: string;
  validFrom: string;
  validTo: string;
  isAvailable: boolean;
}

/** Validated/transformed shape passed to the submit handler (post-resolver). */
export type ContractPriceFormValues = z.infer<typeof contractPriceFormSchema>;

export const DEFAULT_PRICE_FORM_VALUES: ContractPriceFormFields = {
  menuId: '',
  unitPrice: '',
  vatRate: '',
  validFrom: '',
  validTo: '',
  isAvailable: true,
};

/**
 * Mirrors `apps/api` `addContractServiceTypePriceSchema`/
 * `updateContractServiceTypePriceSchema`
 * (`contract-service-type-price.dto.ts`); same pesos-to-cents transform as
 * `contractPriceFormSchema`, `menuId` replaced by `serviceTypeId`.
 */
export const contractServiceTypePriceFormSchema = z.object({
  serviceTypeId: z
    .string()
    .min(1, 'Seleccioná un tipo de servicio')
    .uuid({ message: 'Seleccioná un tipo de servicio' }),
  billingConceptId: z
    .string()
    .min(1, 'Seleccioná un concepto de facturación')
    .uuid({ message: 'Seleccioná un concepto de facturación' }),
  unitPrice: unitPriceSchema,
  vatRate: vatRateSchema,
  validFrom: z.string().trim().min(1, 'La vigencia desde es obligatoria'),
  validTo: z.string().trim(),
  isAvailable: z.boolean(),
});

export interface ContractServiceTypePriceFormFields {
  serviceTypeId: string;
  billingConceptId: string;
  unitPrice: string;
  vatRate: string;
  validFrom: string;
  validTo: string;
  isAvailable: boolean;
}

export type ContractServiceTypePriceFormValues = z.infer<
  typeof contractServiceTypePriceFormSchema
>;

export const DEFAULT_SERVICE_TYPE_PRICE_FORM_VALUES: ContractServiceTypePriceFormFields = {
  serviceTypeId: '',
  billingConceptId: '',
  unitPrice: '',
  vatRate: '',
  validFrom: '',
  validTo: '',
  isAvailable: true,
};
